import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { failed, success } from "../../common/Toastify";
import { useFormik } from "formik";
import { useOrgQuery } from "../../../hooks/ReactQueryHooks/useOrgQuery";
import { FullPageSpinner } from "../../common/Spinner/FullPageSpinner";
import {
  disableServiceLine,
  enableServiceLine,
  getServiceLinesByOrgId,
  editServiceLine,
} from "../../api/ServiceLine";
import SelectFiled from "../../common/textfield/SelectFiled";
import { Col, Row, Table } from "react-bootstrap";
import { TableCell, Tooltip } from "@mui/material";
import CustomPagination from "../../common/components/Pagination/CustomPagination";
import { Link, useNavigate } from "react-router-dom";
import DeleteIcon from "../../../assets/images/delete.png";
import EditIcon from "../../../assets/images/edit.png";
import { useConfirmDialog } from "../../../hooks/useConfirmDialog";
import { Button } from "../../common/Button";
import SearchBar from "../../common/components/SearchBar";
import CreateServiceLineModal from "./CreateServiceLineModal";

function ListServiceLines({ orgdata }) {
  const [serviceLinesData, setServiceLinesData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [page, setPage] = useState(0);
  const rowsPerPage = 10;
  const totalRows = filteredData?.length;
  const [isPageLoading, setIsPageLoading] = useState(false);
  const { getConfirmation } = useConfirmDialog();
  const navigate = useNavigate();
  const userRoles = useSelector(
    (state) => state?.auth?.user?.["cognito:groups"]
  );
  const isPlatformAdmin = userRoles?.includes("Super-Admin");
  const isOrgAdmin = useSelector(
    state => (state?.auth?.user?.permissions?.find((item) => item === "Manage Organization")) ? true : false
  )

  const [refreshFlag, setRefreshFlag] = useState(false);
  const [modalShow, setModalShow] = useState(false);
  const [defaultServiceLine, setDefaultServiceLine] = useState({});

  const formik = useFormik({
    initialValues: {
      orgId: orgdata?.id,
      active: true,
    },
  });

  const STATUS_OPTIONS = [
    {
      name: "Active",
      label: "Active",
      value: true,
    },
    {
      name: "Inactive",
      label: "Inactive",
      value: false,
    },
  ];

  const handleStatusChange = (event) => {
    let value = event?.target?.value;
    formik.setFieldValue("active", value);
    setPage(0);
  };

  const handleDisableClick = async (data) => {
    const confirmed = await getConfirmation({
      title: "Attention!",
      data,
      actionBtnTitle: "Deactivate",
      message: `Are you sure you would like to deactivate ${data?.name}?`,
    });

    if (confirmed) {
      setIsPageLoading(true);
      disableServiceLine(data?.id, data?.orgId)
        .then((res) => {
          if (res?.status === true) {
            success(res?.message || "Service Line disabled successfully");
            setRefreshFlag((prev) => !prev);
          }
        })
        .catch((res) => {
          failed(
            res?.response?.data?.message ||
              res?.response?.data?.error ||
              res.message
          );
        })
        .finally(() => {
          setIsPageLoading(false);
        });
    }
  };

  const handleEnableClick = async (data) => {
    setIsPageLoading(true);
    enableServiceLine(data?.id, data?.orgId)
      .then((res) => {
        if (res?.status === true) {
          success(res?.message || "Service Line enabled successfully");
          setRefreshFlag((prev) => !prev);
        }
      })
      .catch((res) => {
        failed(
          res?.response?.data?.message ||
            res?.response?.data?.error ||
            res.message
        );
      })
      .finally(() => {
        setIsPageLoading(false);
      });
  };
  const handleFilterData = (data) => {
    setFilteredData(data);
  };

  const handleCreateClick = async () => {
    setModalShow(true);
  };

  const handleEditClick = async (data) => {
    navigate(
      `/app/edit-service-line?serviceLineId=${data?.id}&orgId=${orgdata?.id}`
    );
  };

  const orgIdValue = formik.values?.orgId;
  const activeValue = formik.values?.active;

  useEffect(() => {
    if (orgIdValue !== undefined && modalShow === false) {
      setIsPageLoading(true);
      getServiceLinesByOrgId(orgIdValue, activeValue)
        .then((res) => {
          if (res?.status === true) {
            setServiceLinesData(res?.data);
            setFilteredData(res?.data);
            if (res?.data?.length > 0) {
              res?.data?.map((item) => {
                if (item?.isDefault) {
                  setDefaultServiceLine(item);
                }
              });
            }
          }
        })
        .catch((res) => {
          failed(res?.error);
        })
        .finally(() => setIsPageLoading(false));
    }
  }, [orgIdValue, activeValue, refreshFlag, modalShow]);

  const handleSetDefaultClick = async (item) => {
    setIsPageLoading(true);
    // Removing default from existing
    if (defaultServiceLine?.id) {
      editServiceLine(orgdata?.id, defaultServiceLine?.id, {
        isDefault: false,
      })
        .then((res) => {
          // Setting default for the new one
          console.log("Updated");
        })
        .catch((error) => {
          failed(
            error?.error || error?.message || "Error updating Service Lines"
          );
        })
        .finally(() => setIsPageLoading(false));
    }

    setIsPageLoading(true);
    editServiceLine(orgdata?.id, item?.id, { isDefault: true })
      .then((res) => {
        if (res?.status === true) {
          success(res?.message || "Service Line enabled successfully");
          setRefreshFlag((prev) => !prev);
        }
      })
      .catch((error) => {
        failed(
          error?.error || error?.message || "Error updating Service Lines"
        );
      })
      .finally(() => setIsPageLoading(false));
  };

  return (
    <section>
      {isPageLoading && <FullPageSpinner loadingText="Loading..." />}
      {modalShow && (
        <CreateServiceLineModal
          onHide={() => setModalShow(false)}
          onShow={modalShow}
          orgData={orgdata}
        />
      )}
      {/* Menu Bar Options */}
      <div className="px-3">
        <form className="common-form border-fields dropdown-filter-rw">
          <Row className="align-items-end d-flex justify-content-between">
            <Col md="auto" className="d-flex gap-3 align-items-end">
              <div className="mb-4">
                <SearchBar
                  page={page}
                  setPage={setPage}
                  list={serviceLinesData}
                  filterData={handleFilterData}
                />
              </div>
              <div className="mt-2">
                <SelectFiled
                  keyField={"active"}
                  formik={formik}
                  selectLabel={"Status"}
                  options={STATUS_OPTIONS}
                  onChange={handleStatusChange}
                  required={false}
                  optional={false}
                />
              </div>
            </Col>

            {isPlatformAdmin && (
              <Col md="auto">
                <Button
                  onClick={handleCreateClick}
                  className="custom-btn"
                  title="Add Service Line"
                  style={{ width: "auto" }}
                >
                  Create Service Line
                </Button>
              </Col>
            )}
          </Row>
        </form>
      </div>

      {/* Data table starts here*/}
      <div className="table-wrap">
        <Table responsive>
          <thead>
            <tr>
              <th>Name</th>
              <th>Description</th>
              <th style={{ textAlign: "left" }}>Action</th>
            </tr>
          </thead>
          <tbody>
            {filteredData?.length > 0 ? (
              filteredData
                ?.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                ?.map((data) => {
                  return (
                    <tr key={data?.id}>
                      <td>
                        <span>{data?.name}</span>
                        {data?.isDefault === true && (
                          <span
                            style={{
                              marginLeft: "8px",
                              padding: "2px 8px",
                              backgroundColor: "#e0e0e0",
                              color: "#000",
                              borderRadius: "4px",
                              fontSize: "12px",
                              fontWeight: "500",
                            }}
                          >
                            Default
                          </span>
                        )}
                      </td>
                      <td>{data?.description}</td>
                      <td>
                        <div
                          className="action-wrap"
                          style={{ alignItems: "left", justifyContent: "left"
                           }}
                        >
                          {(data?.active === true ||
                            data?.active === "true") && (
                            <Tooltip title="Edit">
                              <div
                                onClick={() => handleEditClick(data)}
                                style={{
                                  display: "inline-flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  backgroundColor: "#0cb359",
                                  borderRadius: "50%",
                                  width: "32px",
                                  height: "32px",
                                  cursor: "pointer",
                                }}
                              >
                                <img
                                  src={EditIcon}
                                  alt="Edit"
                                  style={{
                                    width: "16px",
                                    height: "16px",
                                  }}
                                />
                              </div>
                            </Tooltip>
                          )}

                          {(data?.active === true || data?.active === "true") &&
                            (isPlatformAdmin || isOrgAdmin) &&
                            !data?.isDefault && (
                              <Tooltip title="Deactivate">
                                <div
                                  onClick={() => handleDisableClick(data)}
                                  style={{
                                    display: "inline-flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    backgroundColor: "#ff0000",
                                    borderRadius: "50%",
                                    width: "32px",
                                    height: "32px",
                                    cursor: "pointer",
                                  }}
                                >
                                  <img
                                    src={DeleteIcon}
                                    alt="Deactivate"
                                    style={{
                                      width: "16px",
                                      height: "16px",
                                    }}
                                  />
                                </div>
                              </Tooltip>
                            )}

                          {(data?.active === true || data?.active === "true") &&
                            !data?.isDefault && (
                              <Tooltip title="Set as Default">
                                <button
                                  onClick={() => handleSetDefaultClick(data)}
                                  style={{
                                    backgroundColor: "#fff",
                                    color: "#007bff", // Bootstrap blue
                                    border: "1px solid #007bff",
                                    borderRadius: "4px",
                                    padding: "4px 8px",
                                    fontSize: "12px",
                                    cursor: "pointer",
                                    marginLeft: "8px", // Optional spacing from other icons
                                  }}
                                >
                                  Set as Default
                                </button>
                              </Tooltip>
                            )}

                          {(data?.active === false ||
                            data?.active === "false") &&
                            (isPlatformAdmin || isOrgAdmin) && (
                              <Button
                                variant="primary"
                                onClick={() => handleEnableClick(data)}
                              >
                                Activate
                              </Button>
                            )}
                        </div>
                      </td>
                    </tr>
                  );
                })
            ) : (
              <tr>
                <TableCell colSpan="5" style={{ textAlign: "center" }}>
                  No Data Available
                </TableCell>
              </tr>
            )}
          </tbody>
        </Table>
      </div>

      {/* Pagination Component */}
      {filteredData?.length > 0 ? (
        <CustomPagination
          tableData={filteredData}
          totalRows={totalRows}
          rowsPerPage={rowsPerPage}
          page={page}
          handlePage={(pageNo) => setPage(pageNo)}
        />
      ) : null}
    </section>
  );
}

export default ListServiceLines;
